import type { ReactNode } from "react";
import { codeColors as C } from "../theme/tokens";

export type CodeLanguage = "json" | "ts" | "python" | "shell" | "swift" | "kotlin" | "html" | "http";

const KEYWORDS: Record<Exclude<CodeLanguage, "json" | "html" | "http">, string[]> = {
  ts: ["import", "from", "export", "const", "let", "var", "async", "await", "function", "return", "if", "else", "for", "of", "in", "new", "throw", "try", "catch", "typeof", "type", "interface", "true", "false", "null", "undefined"],
  python: ["import", "from", "def", "return", "if", "elif", "else", "for", "in", "while", "break", "try", "except", "raise", "pass", "with", "as", "not", "and", "or", "True", "False", "None"],
  shell: ["curl", "npm", "npx", "export"],
  swift: ["import", "let", "var", "func", "struct", "return", "if", "else", "for", "in", "guard", "try", "throws", "true", "false", "nil"],
  kotlin: ["import", "val", "var", "fun", "data", "class", "return", "if", "else", "for", "in", "when", "true", "false", "null"],
};

const STRING = String.raw`"(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'|\x60(?:[^\x60\\]|\\.)*\x60`;
const NUMBER = String.raw`-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b`;

const patterns = new Map<CodeLanguage, RegExp>();
function pattern(lang: CodeLanguage): RegExp {
  let re = patterns.get(lang);
  if (!re) {
    if (lang === "json") {
      re = new RegExp(String.raw`("(?:[^"\\]|\\.)*")(\s*:)?|\b(true|false)\b|\b(null)\b|(${NUMBER})|([{}\[\],])`, "g");
    } else if (lang === "http") {
      re = /^([\w-]+)(:)(.*)$/gm;
    } else if (lang === "html") {
      re = /(<!--[\s\S]*?-->)|("[^"]*"|'[^']*')|(<\/?[\w-]+|\/?>)|([\w:-]+)(?==)/g;
    } else {
      const comment = lang === "python" || lang === "shell" ? String.raw`#[^\n]*` : String.raw`\/\/[^\n]*`;
      // Python strings can start with an f/r/b prefix.
      const str = lang === "python" ? String.raw`(?:\b[fbr])?(?:${STRING})` : STRING;
      re = new RegExp(`(${comment})|(${str})|\\b(${KEYWORDS[lang].join("|")})\\b|(${NUMBER})|(\\$[A-Z_][A-Z0-9_]*)`, "g");
    }
    patterns.set(lang, re);
  }
  re.lastIndex = 0;
  return re;
}

const span = (key: number, color: string, text: string, italic = false) => (
  <span key={key} style={{ color, fontStyle: italic ? "italic" : undefined }}>
    {text}
  </span>
);

/** Colour-codes JSON or a code snippet with the same palette as the JSON tree. */
export function highlight(code: string, lang: CodeLanguage): ReactNode[] {
  const out: ReactNode[] = [];
  const re = pattern(lang);
  let last = 0;
  let k = 0;
  for (let m = re.exec(code); m; m = re.exec(code)) {
    if (m[0] === "") {
      re.lastIndex++;
      continue;
    }
    if (m.index > last) out.push(code.slice(last, m.index));
    if (lang === "json") {
      if (m[1] !== undefined) {
        out.push(span(k++, m[2] ? C.key : C.string, m[1]));
        if (m[2]) out.push(span(k++, C.punctuation, m[2]));
      } else if (m[3]) out.push(span(k++, C.boolean, m[0]));
      else if (m[4]) out.push(span(k++, C.null, m[0]));
      else if (m[5]) out.push(span(k++, C.number, m[0]));
      else out.push(span(k++, C.punctuation, m[0]));
    } else if (lang === "http") {
      out.push(span(k++, C.key, m[1]), span(k++, C.punctuation, m[2]), span(k++, C.string, m[3]));
    } else if (lang === "html") {
      if (m[1]) out.push(span(k++, C.null, m[0], true));
      else if (m[2]) out.push(span(k++, C.string, m[0]));
      else if (m[3]) out.push(span(k++, C.key, m[0]));
      else out.push(span(k++, C.boolean, m[0]));
    } else if (m[1]) out.push(span(k++, C.null, m[0], true));
    else if (m[2]) out.push(span(k++, C.string, m[0]));
    else if (m[3]) out.push(span(k++, C.key, m[0]));
    else if (m[4]) out.push(span(k++, C.number, m[0]));
    else out.push(span(k++, C.boolean, m[0]));
    last = re.lastIndex;
  }
  if (last < code.length) out.push(code.slice(last));
  return out;
}
